import type { EmbeddingProvider } from '@fabric-harness/sdk';
import type { ModelServingQueryClient } from '@databricks/sdk-modelservingquery/v1';

export type DatabricksModelServingQueryClient = Pick<ModelServingQueryClient, 'query'>;

export interface DatabricksEmbeddingsOptions {
  client: DatabricksModelServingQueryClient;
  /** Serving-endpoint name of the embedding model. */
  endpoint: string;
  name?: string;
}

/**
 * Embeddings via a Mosaic AI serving **embedding** endpoint. Databricks exposes the OpenAI-compatible
 * embeddings surface at `<host>/serving-endpoints/embeddings` with the endpoint name as `model`.
 * Reuses the bundle's generated Model Serving Query client, so it inherits the rotating
 * UC-principal credential.
 *
 * (URL/response schema worth a smoke test against a live embedding endpoint — chat and embeddings
 * share the OpenAI-compatible convention but endpoint families can differ.)
 */
export function databricksEmbeddings(options: DatabricksEmbeddingsOptions): EmbeddingProvider {
  return {
    name: options.name ?? 'databricks',
    async embed(texts: string[]): Promise<number[][]> {
      if (texts.length === 0) return [];
      const response = await options.client.query({ name: options.endpoint, input: texts });
      const data = [...(response.data ?? [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      return data.map((entry, index) => {
        if (!entry.embedding)
          throw new Error(`Databricks embedding response item ${index} did not contain a vector.`);
        return entry.embedding;
      });
    },
  };
}
